// Auth state machine — Option A (full Cognito flows).
//
// State transitions (idle is the bootstrap-from-storage state):
//
//   idle ─────► unauthenticated
//                │
//                ├─ login() ──► submitting-login
//                │              ├─ tokens          ──► authenticated
//                │              ├─ MFA_CHALLENGE   ──► mfa-challenge
//                │              ├─ NEW_PASSWORD    ──► new-password-required
//                │              └─ MFA_SETUP       ──► mfa-setup-required (no QR yet)
//                │
//                ├─ requestPasswordReset()  ──► forgot-password-sent
//                │   └─ confirmPasswordReset()  ──► submitting-reset ──► unauthenticated
//                │
//   mfa-challenge ──submitMfaCode()──► submitting-mfa
//                                       ├─ tokens          ──► authenticated
//                                       └─ wrong code      ──► mfa-challenge (with error)
//
//   new-password-required ──submitNewPassword()──► submitting-new-password
//                                                   ├─ tokens          ──► authenticated
//                                                   ├─ MFA_CHALLENGE   ──► mfa-challenge
//                                                   └─ MFA_SETUP       ──► mfa-setup-required
//
//   mfa-setup-required ──enrollMfa()──► mfa-setup-pending (QR shown)
//                                        └─ verifyMfaSetup() ──► submitting-mfa-verify
//                                                                ├─ tokens   ──► authenticated
//                                                                └─ chained MFA challenge ──► mfa-challenge
//
//   authenticated ──signOut()──► unauthenticated
//   authenticated ──API 401 / idle 20m──► session-expired (modal)
//   session-expired ──reauth()──► authenticated (replays queued request)
//   session-expired ──signOutFromExpiry()──► unauthenticated
//
// Screens stay presentational: they emit intents, this hook decides the next
// state from Cognito responses. Errors carry stable codes the UI can branch on.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  associateSoftwareToken,
  associateSoftwareTokenWithAccessToken,
  completeMfaSetup,
  confirmForgotPassword as cognitoConfirmForgotPassword,
  forgotPassword as cognitoForgotPassword,
  refreshTokens,
  respondToNewPasswordChallenge,
  setUserMfaPreferenceSoftwareToken,
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  startMfaRecovery as cognitoStartMfaRecovery,
  submitMfaCode as cognitoSubmitMfaCode,
  submitMfaRecoveryCode as cognitoSubmitMfaRecoveryCode,
  verifySoftwareToken,
  verifySoftwareTokenWithAccessToken,
} from './cognito.js';
import type { CognitoAuthError, SignInResult } from './cognito.js';
import {
  clearTokens,
  loadTokens,
  saveTokens,
} from './tokenStorage.js';
import type { StoredTokens } from './tokenStorage.js';

// ─── Public state model ─────────────────────────────────────────────────────

export type AuthStatus =
  | { kind: 'idle' }                                                    // initial mount, before bootstrap
  | { kind: 'unauthenticated' }                                         // no session
  | { kind: 'submitting-login' }                                        // login() in flight
  | { kind: 'mfa-challenge'; email: string; session: string }           // awaiting TOTP
  | { kind: 'submitting-mfa' }
  | { kind: 'new-password-required'; email: string; session: string }   // first-login force password change
  | { kind: 'submitting-new-password' }
  | { kind: 'mfa-setup-required'; email: string; session: string }      // user must enroll TOTP to continue
  | { kind: 'mfa-setup-pending';                                        // QR shown; awaiting first TOTP code
      email: string;
      session: string;
      secretCode: string;
      otpauthUri: string;
    }
  | { kind: 'submitting-mfa-verify' }
  // Custom Auth Flow — MFA recovery via email code.
  // 1. User clicks "I lost my authenticator" → startMfaRecovery fires
  //    InitiateAuth(CUSTOM_AUTH); Cognito emails a 6-digit code; state →
  //    mfa-recovery-code-sent.
  | { kind: 'mfa-recovery-code-sent'; email: string; session: string; emailHint?: string }
  | { kind: 'submitting-mfa-recovery-code' }
  // 2. Verified code → Cognito issues real tokens. We hold them + call
  //    AssociateSoftwareToken(AccessToken) to begin fresh TOTP enrollment,
  //    showing the QR. State → mfa-reenrollment-pending.
  | { kind: 'mfa-reenrollment-pending';
      email: string;
      accessToken: string;
      stagedTokens: StoredTokens;
      secretCode: string;
      otpauthUri: string;
    }
  | { kind: 'submitting-mfa-reenrollment' }
  | { kind: 'forgot-password-sent'; email: string }                     // reset email dispatched
  | { kind: 'submitting-reset' }
  | { kind: 'authenticated'; tokens: StoredTokens }
  | { kind: 'session-expired'; previousEmail: string };                 // modal overlay

export interface AuthError {
  code: CognitoAuthError['code'];
  message: string;
}

export interface AuthFlowApi {
  status: AuthStatus;
  error: AuthError | null;
  /** Submit email + password. */
  login: (email: string, password: string) => Promise<void>;
  /** Submit the 6-digit TOTP code at the login MFA challenge. */
  submitMfaCode: (code: string) => Promise<void>;
  /** Force-change the password on first login. */
  submitNewPassword: (newPassword: string) => Promise<void>;
  /** Start TOTP enrollment (calls AssociateSoftwareToken; shows QR). */
  enrollMfa: () => Promise<void>;
  /** Verify the TOTP code during enrollment; on success completes the MFA_SETUP challenge. */
  verifyMfaSetup: (code: string) => Promise<void>;
  /**
   * Begin MFA recovery via the Custom Auth Flow — called when the user is
   * stuck at a SOFTWARE_TOKEN_MFA challenge they can't satisfy. Fires
   * InitiateAuth(CUSTOM_AUTH); Cognito's CreateAuthChallenge trigger emails
   * a 6-digit code. Transitions to mfa-recovery-code-sent.
   */
  startMfaRecovery: () => Promise<void>;
  /**
   * Submit the 6-digit code the user received by email. On success,
   * Cognito issues real tokens; we immediately kick off fresh TOTP
   * enrollment (AssociateSoftwareToken(AccessToken)) and transition to
   * mfa-reenrollment-pending. The user doesn't land in the app with a
   * broken MFA state — they re-enroll a device first.
   */
  submitMfaRecoveryCode: (code: string) => Promise<void>;
  /**
   * Verify the new TOTP code during post-recovery re-enrollment; writes
   * SetUserMFAPreference and finally transitions to authenticated.
   */
  verifyMfaReenrollment: (code: string) => Promise<void>;
  /**
   * Trigger a forgot-password email. Always neutral (no enumeration).
   * `locale` propagates to the CustomEmailSender Lambda so the reset
   * email is rendered in the user's UI language.
   */
  requestPasswordReset: (email: string, locale?: 'en' | 'fr') => Promise<void>;
  /** Complete the password reset using the emailed code. */
  confirmPasswordReset: (email: string, code: string, newPassword: string) => Promise<void>;
  /** Re-authenticate from the session-expired modal. */
  reauth: (password: string) => Promise<void>;
  /** Sign out (also dismisses session-expired modal). */
  signOut: () => Promise<void>;
  /** Programmatically trigger the session-expired modal (e.g. from API client on 401). */
  flagSessionExpired: () => void;
  /** Clear the visible error (useful when the user starts typing again). */
  clearError: () => void;
}

// ─── Internals ──────────────────────────────────────────────────────────────

// Refresh the access token this many ms before it expires.
const REFRESH_LEAD_MS = 60_000;
// Auto-trigger session-expired modal after this long without user activity.
const IDLE_TIMEOUT_MS = 20 * 60 * 1000; // 20 min per auth handoff

function buildOtpauthUri(email: string, secret: string): string {
  // Issuer = "GC Coach"; label = "GC Coach:<email>". URI is what an
  // authenticator app expects when scanning a QR code.
  const issuer = encodeURIComponent('GC Coach');
  const account = encodeURIComponent(email);
  return `otpauth://totp/${issuer}:${account}?secret=${secret}&issuer=${issuer}&algorithm=SHA1&digits=6&period=30`;
}

// Pull the `email` claim out of a Cognito id_token without verifying the
// signature. Safe because the id_token is something WE just received from
// Cognito over TLS and stored in our own sessionStorage — we're reading
// our own data, not validating an attacker's input. Used by the bootstrap
// path to repopulate pendingEmailRef when the SPA hydrates from storage
// (page refresh, new tab); without this, the session-expired modal's
// reauth() call would send USERNAME="" to Cognito and 400.
function emailFromIdToken(idToken: string): string | null {
  try {
    const payload = idToken.split('.')[1];
    if (!payload) return null;
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(decoded) as { email?: unknown };
    return typeof claims.email === 'string' ? claims.email : null;
  } catch {
    return null;
  }
}

export function useAuthFlow(): AuthFlowApi {
  const [status, setStatus] = useState<AuthStatus>({ kind: 'idle' });
  const [error, setError] = useState<AuthError | null>(null);

  // statusRef trick: intents need to read the latest status synchronously
  // without re-creating themselves (which would invalidate consumer callbacks).
  //
  // We update the ref DURING RENDER, not in a useEffect — otherwise child
  // effects fire before our parent useEffect runs, and the ref is stale.
  // Concretely: when status transitions to mfa-setup-required, the
  // MFASetupWizard's mount effect calls enrollMfa() in the same React
  // commit. If statusRef.current were updated in a useEffect here, that
  // effect would run AFTER the wizard's effect (children-first effect
  // order), so enrollMfa() would read statusRef.current === the previous
  // status ('submitting-new-password') and bail with "Not in MFA-setup
  // state". Updating the ref synchronously during render fixes the
  // ordering — refs are mutable and writing them during render doesn't
  // trigger a re-render, which is the canonical "track latest state in
  // a ref" pattern.
  const statusRef = useRef(status);
  statusRef.current = status;

  // Pending-MFA email is kept for the RespondToAuthChallenge call.
  const pendingEmailRef = useRef<string | null>(null);
  const refreshTimerRef = useRef<number | null>(null);
  const idleTimerRef = useRef<number | null>(null);

  // ─── Refresh + idle scheduling ───────────────────────────────────────────

  const scheduleRefresh = useCallback((tokens: StoredTokens) => {
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
    }
    const delay = Math.max(0, tokens.expiresAt - Date.now() - REFRESH_LEAD_MS);
    refreshTimerRef.current = window.setTimeout(async () => {
      try {
        const next = await refreshTokens(tokens.refreshToken);
        const nextStored: StoredTokens = {
          accessToken: next.accessToken,
          idToken: next.idToken,
          refreshToken: tokens.refreshToken,
          expiresAt: next.expiresAt,
        };
        saveTokens(nextStored);
        setStatus({ kind: 'authenticated', tokens: nextStored });
        scheduleRefresh(nextStored);
      } catch {
        // Refresh failed — drop session, kick to session-expired modal so the
        // user can reauth without losing the route they were on.
        flagSessionExpiredRef.current?.();
      }
    }, delay);
  }, []);

  const flagSessionExpiredRef = useRef<(() => void) | null>(null);

  const flagSessionExpired = useCallback(() => {
    // Only meaningful if currently authenticated.
    const current = statusRef.current;
    if (current.kind !== 'authenticated') return;
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    // Tokens are stale by definition — clear them so any in-flight requests
    // that retry don't reuse them.
    clearTokens();
    // Preserve the user's email so the modal can show it read-only.
    const previousEmail = pendingEmailRef.current ?? '';
    setStatus({ kind: 'session-expired', previousEmail });
  }, []);

  // Keep ref in sync so scheduleRefresh's stale closure can call it.
  useEffect(() => {
    flagSessionExpiredRef.current = flagSessionExpired;
  }, [flagSessionExpired]);

  const scheduleIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current);
    }
    idleTimerRef.current = window.setTimeout(() => {
      flagSessionExpired();
    }, IDLE_TIMEOUT_MS);
  }, [flagSessionExpired]);

  // Reset the idle timer on any user activity. Listeners attach only while
  // authenticated to avoid wasted work pre-login.
  useEffect(() => {
    if (status.kind !== 'authenticated') return;

    const reset = () => scheduleIdleTimer();
    const events: Array<keyof WindowEventMap> = [
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
    ];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    scheduleIdleTimer();

    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [status.kind, scheduleIdleTimer]);

  // ─── Bootstrap from sessionStorage ───────────────────────────────────────

  useEffect(() => {
    const tokens = loadTokens();
    if (tokens && tokens.expiresAt > Date.now() + REFRESH_LEAD_MS) {
      // Recover the user's email from the id_token. Without this, a page
      // refresh hydrates an authenticated session but leaves pendingEmailRef
      // null — so when the 20-min idle timer fires and the SessionExpiredModal
      // pops, reauth() calls Cognito InitiateAuth with USERNAME="" and
      // Cognito returns 400. The SDK retries 3× and the modal shows
      // "Couldn't reauthenticate" with no useful signal as to why.
      pendingEmailRef.current = emailFromIdToken(tokens.idToken);
      setStatus({ kind: 'authenticated', tokens });
      scheduleRefresh(tokens);
    } else {
      if (tokens) clearTokens();
      setStatus({ kind: 'unauthenticated' });
    }
    return () => {
      if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
    };
  }, [scheduleRefresh]);

  // ─── Result handling helper ──────────────────────────────────────────────

  const handleSignInResult = useCallback(
    (result: SignInResult) => {
      switch (result.kind) {
        case 'OK': {
          saveTokens(result.tokens);
          // Don't null pendingEmailRef on auth success — we need it for the
          // SessionExpiredModal's ACCOUNT field if the session later
          // expires. Initial sign-in flow set it; we keep it through the
          // authenticated session.
          setStatus({ kind: 'authenticated', tokens: result.tokens });
          scheduleRefresh(result.tokens);
          return;
        }
        case 'MFA_CHALLENGE':
          pendingEmailRef.current = result.email;
          setStatus({ kind: 'mfa-challenge', email: result.email, session: result.session });
          return;
        case 'NEW_PASSWORD_REQUIRED':
          pendingEmailRef.current = result.email;
          setStatus({
            kind: 'new-password-required',
            email: result.email,
            session: result.session,
          });
          return;
        case 'MFA_SETUP_REQUIRED':
          pendingEmailRef.current = result.email;
          setStatus({
            kind: 'mfa-setup-required',
            email: result.email,
            session: result.session,
          });
          return;
      }
    },
    [scheduleRefresh],
  );

  const handleError = useCallback((err: unknown, fallbackState: AuthStatus) => {
    const e = err as CognitoAuthError;
    setError({ code: e.code, message: e.message });
    setStatus(fallbackState);
  }, []);

  // For errors raised DURING a Cognito challenge step (MFA challenge, MFA
  // setup, NEW_PASSWORD_REQUIRED response). Cognito raises
  // NotAuthorizedException → INVALID_CREDENTIALS when the challenge session
  // token has been invalidated (expired session, credentials rejected on
  // resubmit) — that means the whole auth attempt is dead and the user must
  // re-enter their email + password from scratch. Retry-able errors
  // (WRONG_CODE for wrong TOTP, WEAK_PASSWORD for policy violations,
  // RATE_LIMITED) keep the user on the challenge screen so they can try again
  // without losing progress.
  const handleChallengeError = useCallback(
    (err: unknown, currentChallenge: AuthStatus) => {
      const e = err as CognitoAuthError;
      setError({ code: e.code, message: e.message });
      if (e.code === 'INVALID_CREDENTIALS') {
        setStatus({ kind: 'unauthenticated' });
        return;
      }
      setStatus(currentChallenge);
    },
    [],
  );

  // ─── Intents ─────────────────────────────────────────────────────────────

  const login = useCallback(
    async (email: string, password: string) => {
      // Stash the email up-front, before any Cognito round-trip. Covers
      // every downstream path (one-shot success, MFA challenge, NEW_PASSWORD,
      // MFA_SETUP) without relying on handleSignInResult's per-challenge
      // branches to populate pendingEmailRef. Means the SessionExpiredModal
      // always has the correct email even if the user signed in without
      // any challenge step (e.g. an admin without MFA, or a future flow).
      pendingEmailRef.current = email;
      setError(null);
      setStatus({ kind: 'submitting-login' });
      try {
        const result = await cognitoSignIn(email, password);
        handleSignInResult(result);
      } catch (err) {
        handleError(err, { kind: 'unauthenticated' });
      }
    },
    [handleSignInResult, handleError],
  );

  const submitMfaCode = useCallback(
    async (code: string) => {
      const current = statusRef.current;
      if (current.kind !== 'mfa-challenge') {
        setError({ code: 'UNKNOWN', message: 'No MFA challenge in flight' });
        return;
      }
      setError(null);
      setStatus({ kind: 'submitting-mfa' });
      try {
        const tokens = await cognitoSubmitMfaCode(current.session, current.email, code);
        saveTokens(tokens);
        setStatus({ kind: 'authenticated', tokens });
        scheduleRefresh(tokens);
      } catch (err) {
        handleChallengeError(err, current);
      }
    },
    [handleChallengeError, scheduleRefresh],
  );

  const submitNewPassword = useCallback(
    async (newPassword: string) => {
      const current = statusRef.current;
      if (current.kind !== 'new-password-required') {
        setError({ code: 'UNKNOWN', message: 'No new-password challenge in flight' });
        return;
      }
      setError(null);
      setStatus({ kind: 'submitting-new-password' });
      try {
        const result = await respondToNewPasswordChallenge(
          current.session,
          current.email,
          newPassword,
        );
        handleSignInResult(result);
      } catch (err) {
        handleChallengeError(err, current);
      }
    },
    [handleSignInResult, handleChallengeError],
  );

  const enrollMfa = useCallback(async () => {
    const current = statusRef.current;
    if (current.kind !== 'mfa-setup-required') {
      setError({ code: 'UNKNOWN', message: 'Not in MFA-setup state' });
      return;
    }
    setError(null);
    try {
      const { secretCode, session: nextSession } = await associateSoftwareToken(current.session);
      setStatus({
        kind: 'mfa-setup-pending',
        email: current.email,
        session: nextSession,
        secretCode,
        otpauthUri: buildOtpauthUri(current.email, secretCode),
      });
    } catch (err) {
      handleChallengeError(err, current);
    }
  }, [handleChallengeError]);

  const verifyMfaSetup = useCallback(
    async (code: string) => {
      const current = statusRef.current;
      if (current.kind !== 'mfa-setup-pending') {
        setError({ code: 'UNKNOWN', message: 'Not in MFA-setup-pending state' });
        return;
      }
      setError(null);
      setStatus({ kind: 'submitting-mfa-verify' });
      try {
        const { session: verifiedSession } = await verifySoftwareToken(current.session, code);
        const result = await completeMfaSetup(verifiedSession, current.email);
        // Promote the just-enrolled TOTP to the user's canonical preferred
        // method. Without this, UserMFASettingList + PreferredMfaSetting
        // stay empty and AdminSetUserMFAPreference reset becomes a no-op,
        // leaving the orphan TOTP association as a permanent lockout.
        // Best-effort: a failure here still signs the user in; their TOTP
        // still works on subsequent challenges, only the admin-side display
        // and the reset flow stay broken until the next successful call.
        if (result.kind === 'OK') {
          try {
            await setUserMfaPreferenceSoftwareToken(result.tokens.accessToken);
          } catch (err) {
            console.warn('[auth] SetUserMFAPreference failed after enrollment', err);
          }
        }
        handleSignInResult(result);
      } catch (err) {
        handleChallengeError(err, current);
      }
    },
    [handleSignInResult, handleChallengeError],
  );

  // ── MFA recovery via Custom Auth Flow (email code + fresh enrollment) ──
  //
  // The earlier device-recovery flow (AssociateSoftwareToken on the mid-
  // SOFTWARE_TOKEN_MFA session) was blocked by Cognito returning
  // "Invalid session for the user" — the challenge session is scoped to
  // responding to that specific challenge, not to new enrollment. The
  // Custom Auth Flow replaces that: a 6-digit code is emailed via our
  // CustomAuthChallenge Lambda trio, the user enters it, Cognito issues
  // real tokens, and we immediately walk them through TOTP re-enrollment
  // using AssociateSoftwareToken(AccessToken) — which Cognito DOES accept.

  const startMfaRecovery = useCallback(async () => {
    const current = statusRef.current;
    if (current.kind !== 'mfa-challenge') {
      setError({ code: 'UNKNOWN', message: 'Not in mfa-challenge state' });
      return;
    }
    setError(null);
    try {
      const { session, emailHint } = await cognitoStartMfaRecovery(current.email);
      setStatus({
        kind: 'mfa-recovery-code-sent',
        email: current.email,
        session,
        ...(emailHint ? { emailHint } : {}),
      });
    } catch (err) {
      handleChallengeError(err, current);
    }
  }, [handleChallengeError]);

  const submitMfaRecoveryCode = useCallback(
    async (code: string) => {
      const current = statusRef.current;
      if (current.kind !== 'mfa-recovery-code-sent') {
        setError({ code: 'UNKNOWN', message: 'Not in mfa-recovery-code-sent state' });
        return;
      }
      setError(null);
      setStatus({ kind: 'submitting-mfa-recovery-code' });
      try {
        const result = await cognitoSubmitMfaRecoveryCode(
          current.email,
          code,
          current.session,
        );
        if (result.kind !== 'OK') {
          // Shouldn't happen — the cognito wrapper throws WRONG_CODE on
          // retry. Defensive branch resets to code-sent state.
          setStatus({ kind: 'mfa-recovery-code-sent', email: current.email, session: current.session });
          setError({ code: 'UNKNOWN', message: 'Recovery exchange returned no tokens' });
          return;
        }
        // Tokens in hand. Immediately start fresh TOTP enrollment to
        // replace the orphan association that got us stuck in the first
        // place. Don't flip to 'authenticated' yet — the user needs to
        // walk through QR + verify first, otherwise their next sign-in
        // repeats this exact lockout.
        const { secretCode } = await associateSoftwareTokenWithAccessToken(
          result.tokens.accessToken,
        );
        const stagedTokens: StoredTokens = {
          accessToken: result.tokens.accessToken,
          idToken: result.tokens.idToken,
          refreshToken: result.tokens.refreshToken,
          expiresAt: result.tokens.expiresAt,
        };
        setStatus({
          kind: 'mfa-reenrollment-pending',
          email: current.email,
          accessToken: result.tokens.accessToken,
          stagedTokens,
          secretCode,
          otpauthUri: buildOtpauthUri(current.email, secretCode),
        });
      } catch (err) {
        const e = err as CognitoAuthError;
        if (e.code === 'WRONG_CODE') {
          // Cognito re-issued another CUSTOM_CHALLENGE — stay on the code
          // input, surface the error. Session stays the same from Cognito's
          // perspective (DefineAuth increments its attempt count internally).
          setStatus({ kind: 'mfa-recovery-code-sent', email: current.email, session: current.session });
          setError({ code: 'WRONG_CODE', message: e.message });
          return;
        }
        handleChallengeError(err, current);
      }
    },
    [handleChallengeError],
  );

  const verifyMfaReenrollment = useCallback(
    async (code: string) => {
      const current = statusRef.current;
      if (current.kind !== 'mfa-reenrollment-pending') {
        setError({ code: 'UNKNOWN', message: 'Not in mfa-reenrollment-pending state' });
        return;
      }
      setError(null);
      setStatus({ kind: 'submitting-mfa-reenrollment' });
      try {
        await verifySoftwareTokenWithAccessToken(current.accessToken, code);
        // Part A on top of the recovery: lock the preference so admin
        // reset actually works next time + UserMFASettingList is populated.
        try {
          await setUserMfaPreferenceSoftwareToken(current.accessToken);
        } catch (err) {
          console.warn('[auth] SetUserMFAPreference failed after re-enrollment', err);
        }
        // Only NOW does the user transition to signed-in. Store tokens +
        // schedule refresh, same as a normal sign-in success.
        saveTokens(current.stagedTokens);
        setStatus({ kind: 'authenticated', tokens: current.stagedTokens });
        scheduleRefresh(current.stagedTokens);
      } catch (err) {
        const e = err as CognitoAuthError;
        if (e.code === 'WRONG_CODE') {
          setStatus({
            kind: 'mfa-reenrollment-pending',
            email: current.email,
            accessToken: current.accessToken,
            stagedTokens: current.stagedTokens,
            secretCode: current.secretCode,
            otpauthUri: current.otpauthUri,
          });
          setError({ code: 'WRONG_CODE', message: e.message });
          return;
        }
        handleChallengeError(err, current);
      }
    },
    [handleChallengeError, scheduleRefresh],
  );

  const requestPasswordReset = useCallback(async (email: string, locale: 'en' | 'fr' = 'en') => {
    setError(null);
    try {
      await cognitoForgotPassword(email, locale);
    } catch (err) {
      // Only rate-limit + network errors surface; everything else is silent
      // (no enumeration). State still advances either way.
      const e = err as CognitoAuthError;
      setError({ code: e.code, message: e.message });
    }
    // Always end in forgot-password-sent — neutral confirmation regardless.
    setStatus({ kind: 'forgot-password-sent', email });
  }, []);

  const confirmPasswordReset = useCallback(
    async (email: string, code: string, newPassword: string) => {
      setError(null);
      setStatus({ kind: 'submitting-reset' });
      try {
        await cognitoConfirmForgotPassword(email, code, newPassword);
        setStatus({ kind: 'unauthenticated' });
      } catch (err) {
        handleError(err, { kind: 'forgot-password-sent', email });
      }
    },
    [handleError],
  );

  const reauth = useCallback(
    async (password: string) => {
      const current = statusRef.current;
      if (current.kind !== 'session-expired') {
        setError({ code: 'UNKNOWN', message: 'Not in session-expired state' });
        return;
      }
      if (!current.previousEmail) {
        // Defensive: bootstrap normally repopulates this from the id_token.
        // If it didn't (token decode failed, sessionStorage corruption),
        // sending USERNAME="" to Cognito returns 400 with no useful copy.
        // Force a sign-out instead so the user can re-enter their email.
        setError({
          code: 'UNKNOWN',
          message: 'Session recovery failed — please sign in again.',
        });
        return;
      }
      setError(null);
      try {
        const result = await cognitoSignIn(current.previousEmail, password);
        handleSignInResult(result);
      } catch (err) {
        handleError(err, current);
      }
    },
    [handleSignInResult, handleError],
  );

  const signOut = useCallback(async () => {
    const current = statusRef.current;
    if (current.kind === 'authenticated') {
      await cognitoSignOut(current.tokens.accessToken);
    }
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (idleTimerRef.current) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    pendingEmailRef.current = null;
    clearTokens();
    setStatus({ kind: 'unauthenticated' });
    setError(null);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    status,
    error,
    login,
    submitMfaCode,
    submitNewPassword,
    enrollMfa,
    verifyMfaSetup,
    startMfaRecovery,
    submitMfaRecoveryCode,
    verifyMfaReenrollment,
    requestPasswordReset,
    confirmPasswordReset,
    reauth,
    signOut,
    flagSessionExpired,
    clearError,
  };
}
